/**
 * Tracks whether the browser is on its way off the current page.
 *
 * Why this exists: a full-page navigation cancels every request still in flight, including
 * the module request for a `React.lazy` route that happens to be mounting at that moment.
 * React rethrows that cancellation during render, the root ErrorBoundary catches it, and the
 * user is shown "Something went wrong" for however long the navigation takes — even though
 * nothing is broken and the page they asked for is already on its way. Sign-out hit this
 * every single time: it renders `/login`, then immediately redirects to the AuthKit logout
 * URL, killing that page's chunk request. The error boundary consults this flag so a crash
 * on the way out is treated as noise rather than reported to the user as a failure.
 */
let leavingPage = false;

/**
 * Announce a deliberate full-page navigation. Call this immediately before assigning
 * `window.location`, not from an unload listener: for a cross-origin destination the browser
 * fires `pagehide`/`beforeunload` only once the new document is ready to commit, which is long
 * after it has cancelled the requests we care about.
 */
export function markLeavingPage(): void {
  leavingPage = true;
}

export function isLeavingPage(): boolean {
  return leavingPage;
}

/**
 * Backstop for navigations the app did not initiate — a link click, the back button, closing
 * the tab. These land too late to help the lazy-chunk case above, but they still cover a
 * crash that happens after the browser has committed to leaving.
 */
export function installPageExitTracking(): void {
  window.addEventListener('pagehide', markLeavingPage);
  window.addEventListener('beforeunload', markLeavingPage);
}

/** Reset hook for tests; the flag is deliberately one-way in the browser. */
export function resetLeavingPageForTests(): void {
  leavingPage = false;
}
